import React, { useEffect, useRef, useState } from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import InformationOutlineIcon from '../../assets/InformationOutlineIcon';
import useIsBrowser from '../../hooks/useIsBrowser';

type PieChartProps = {
  data: { label: string; value: number; fill: string; text: string }[];
  title?: string;
  description?: string;
};

export default function DynamicPieChart({
  data,
  title,
  description,
}: PieChartProps) {
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const chartContainerRef = useRef(null);
  const isBrowser = useIsBrowser();

  // We need this to avoid Hydratation fail
  useEffect(() => {
    setChartSize({ width: 250, height: 250 });
  }, []);

  const dataForChart = data?.map(
    (obj: { label: string; value: number; text: string }, index: number) => ({
      id: index,
      name: `${obj.label} at birth`,
      value: obj.value,
      text: obj.text,
    })
  );

  const RADIAN = Math.PI / 180;

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    name,
  }: // eslint-disable-next-line
  any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    let color;

    if (percent * 100 === 50 && name?.toLocaleLowerCase()?.includes('female')) {
      color = 'black';
    } else if (percent * 100 === 50 && name?.includes('Male')) {
      color = 'white';
    } else {
      color = percent * 100 > 50 ? 'white' : 'black';
    }

    return (
      <text
        x={x}
        y={y}
        fill={color}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        className="z-50 text-xs"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const renderColorfulLegendText = (
    objs: { name: string; value: number }[]
  ) => {
    return objs.map((obj: { name: string; value: number }) => (
      <div key={obj.name} className="flex w-fit flex-row gap-1">
        <span
          className={`size-4 rounded-full ${getLegendDotColor(obj)}`}
        ></span>
        <span className="text-xs font-normal leading-none text-zinc-600">
          {obj.name}
        </span>
      </div>
    ));
  };

  const getLegendDotColor = (entry: { value: number; name: string }) => {
    if (
      entry.value === 50 &&
      entry.name?.toLocaleLowerCase()?.includes('female')
    ) {
      return 'bg-[#aae5d4]';
    } else if (entry.value === 50 && entry.name?.includes('Male')) {
      return 'bg-[#166534] bg-opacity-100';
    }
    return entry?.value < 50 ? 'bg-[#aae5d4]' : 'bg-[#166534]  bg-opacity-100';
  };

  const getCellColor = (entry: { value: number; name: string }) => {
    if (
      entry.value === 50 &&
      entry.name?.toLocaleLowerCase()?.includes('female')
    ) {
      return 'rgba(170, 229, 212, 1)';
    } else if (entry.value === 50 && entry.name?.includes('Male')) {
      return 'rgba(37, 124, 107, 1)';
    }
    return entry?.value < 50
      ? 'rgba(170, 229, 212, 1)'
      : 'rgba(37, 124, 107, 1)';
  };

  if (!isBrowser) {
    return null;
  }

  if (!data?.length) {
    return null;
  }

  return (
    <div className="flex shrink grow basis-0 flex-col gap-6 rounded-xl bg-white p-6 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)]">
      <div className="flex items-center gap-1">
        <h6 className="text-lg font-semibold">{title}</h6>
        <InformationOutlineIcon fill="#A1A1AA" width={16} height={16} />
      </div>
      <div className="size-full px-2 py-4 lg:px-12">
        <div
          key={`key-${chartSize.width}`}
          ref={chartContainerRef}
          suppressHydrationWarning
          className="flex size-full flex-col items-center justify-center"
        >
          <PieChart width={chartSize.width} height={chartSize.height}>
            <Pie
              data={dataForChart}
              dataKey="value"
              nameKey="name"
              outerRadius={120}
              startAngle={100}
              innerRadius={40}
              endAngle={460}
              labelLine={false}
              fontSize={16}
              fontWeight={600}
              className="outline-none"
              label={renderCustomizedLabel}
              activeIndex={-1}
            >
              {dataForChart.map((entry) => (
                <Cell
                  className="outline-none"
                  key={`cell-${entry.name}`}
                  fill={getCellColor(entry)}
                  color={entry.text}
                  fontSize={16}
                  fontWeight={600}
                  name={entry.name}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: '12px',
                padding: '8px',
                fontSize: '12px',
                fontFamily: "'Montserrat', sans-serif",
              }}
            />
          </PieChart>
        </div>
      </div>
      <div className="flex w-full items-center gap-4">
        {renderColorfulLegendText(dataForChart)}
      </div>
    </div>
  );
}
